/**
 * Labels amigáveis e cores para os estados das entidades do módulo de Inovação.
 *
 * Centraliza o que era espalhado em cada componente como `aguardando_respostas`,
 * `em_revisao_gni`, etc. Use sempre `rotuloEstadoAnalise(estado)` ao mostrar
 * estado pra usuário; mantenha o nome técnico só em chamadas a API.
 */

export const ROTULO_ESTADO_ANALISE = {
  rascunho:              { label: 'Rascunho',                       cor: '#95a5a6', etapa: 0 },
  aguardando_respostas:  { label: 'Pendente com operador',          cor: '#f39c12', etapa: 1 },
  em_revisao_gni:        { label: 'Respondido, em revisão',         cor: '#3498db', etapa: 2 },
  em_aprovacao:          { label: 'Em aprovação',                   cor: '#9b59b6', etapa: 3 },
  aprovada:              { label: 'Aprovada',                       cor: '#27ae60', etapa: 4 },
  projeto_criado:        { label: 'Virou projeto',                  cor: 'var(--brand, #20a490)', etapa: 5 },
  rejeitada:             { label: 'Rejeitada',                      cor: '#e74c3c', etapa: -1 },
  backlog:               { label: 'No backlog',                     cor: '#7f8c8d', etapa: -2 },
  cancelada:             { label: 'Cancelada',                      cor: '#bdc3c7', etapa: -3 },
};

export const ROTULO_ESTADO_PROJETO = {
  planejado:    { label: 'Planejado',     cor: '#95a5a6' },
  aguardando_aprovacao: { label: 'Aguardando aprovação', cor: '#9b59b6' },
  em_execucao:  { label: 'Em execução',   cor: '#3498db' },
  em_risco:     { label: 'Em risco',      cor: '#e67e22' },
  pausado:      { label: 'Pausado',       cor: '#9b59b6' },
  concluido:    { label: 'Concluído',     cor: '#27ae60' },
  cancelado:    { label: 'Cancelado',     cor: '#7f8c8d' },
};

export const ROTULO_ESTADO_FASE = {
  planejada:    { label: 'Planejada',     cor: '#95a5a6' },
  em_execucao:  { label: 'Em execução',   cor: '#3498db' },
  concluida:    { label: 'Concluída',     cor: '#27ae60' },
  em_risco:     { label: 'Em risco',      cor: '#e67e22' },
  bloqueada:    { label: 'Bloqueada',     cor: '#e74c3c' },
};

export function rotuloEstadoAnalise(estado) {
  return ROTULO_ESTADO_ANALISE[estado] || { label: estado, cor: '#95a5a6', etapa: 0 };
}
export function rotuloEstadoProjeto(estado) {
  return ROTULO_ESTADO_PROJETO[estado] || { label: estado, cor: '#95a5a6' };
}
export function rotuloEstadoFase(estado) {
  return ROTULO_ESTADO_FASE[estado] || { label: estado, cor: '#95a5a6' };
}

export const ETAPAS_FUNIL_ANALISE = [
  { chave: 'rascunho',             label: 'Rascunho' },
  { chave: 'aguardando_respostas', label: 'Operador' },
  { chave: 'em_revisao_gni',       label: 'Revisão' },
  { chave: 'em_aprovacao',         label: 'Aprovação' },
  { chave: 'aprovada',             label: 'Aprovada' },
];

// Mapa de códigos de erro do backend para mensagens humanas.
// O backend devolve detail: { code: "NOME_DO_CODIGO", message?, context? }
// — códigos em SCREAMING_SNAKE são canônicos; chaves em snake_case são compat
// com strings antigas de ValueError convertidas a maiúsculas.
export const MENSAGENS_ERRO = {
  NAO_ENCONTRADO: 'Registro não encontrado',
  ANALISE_NAO_ENCONTRADA: 'Análise não encontrada',
  ANALISE_JA_TEM_PROJETO: 'Esta análise já virou projeto',
  ANALISE_NAO_APROVADA: 'A análise precisa estar aprovada para virar projeto',
  PROJETO_TRANSICAO_INVALIDA: 'Transição de estado não permitida neste momento',
  PROJETO_FORA_DO_ESCOPO: 'Apenas responsável técnico, gerente ou admin podem operar este projeto',
  PROJETO_FORA_DO_ESCOPO_VISIBILIDADE: 'Este projeto não está no seu escopo de visibilidade. Peça acesso ao admin.',
  FASE_TRANSICAO_INVALIDA: 'Transição de estado da fase não permitida',
  FASE_FORA_DO_ESCOPO: 'Apenas responsável da fase/projeto, gerente ou admin podem operar esta fase',
  FASE_NAO_PLANEJADA: 'Só é possível remover fase ainda planejada',
  CICLO_DEPENDENCIA: 'A dependência criaria um ciclo entre fases',
  DEPENDENCIA_INEXISTENTE: 'Dependência informada não existe',
  EDICAO_NAO_AUTORIZADA: 'Você não tem permissão para editar este registro',
  COMENTARIO_FORA_DA_JANELA: 'Janela de 15 min expirou; só admin pode alterar/remover',
  TEMPLATE_INATIVO: 'Template selecionado está inativo',
  PREDECESSOR_INVALIDO: 'Projeto predecessor inválido ou cancelado',
  JUSTIFICATIVA_OBRIGATORIA: 'O plano já foi aprovado. Informe a justificativa do desvio',
  CICLO_DEPENDENCIA: 'A dependência criaria um ciclo entre as tarefas',
  OUTRO_PROJETO: 'A predecessora precisa ser do mesmo projeto',
  DEPENDENCIA_SELF: 'Uma tarefa não pode depender de si mesma',
  PRIORIDADE_INVALIDA: 'Prioridade inválida',
  ESTADO_INVALIDO: 'Estado inválido',
  PROJETO_PRECISA_APROVACAO: 'O projeto precisa ser aprovado antes de executar',
  PROJETO_AGUARDANDO_APROVACAO: 'O projeto está aguardando aprovação',
  SEM_APROVADORES_CONFIGURADOS: 'Adicione ao menos um aprovador antes de enviar',
  SEM_FASES: 'O projeto precisa ter ao menos uma fase',
  ARQUIVO_GRANDE: 'Arquivo acima do limite de 20 MB',
  ARQUIVO_VAZIO: 'Arquivo vazio',
};

/**
 * Extrai mensagem amigável de erro do backend Inovação.
 * Aceita `e` (Error do axios), `e.response.data.detail`, ou string.
 * Sempre retorna string — usa fallback genérico se nada bater.
 */
export function mensagemErro(e, fallback = 'Erro inesperado') {
  if (!e) return fallback;
  if (typeof e === 'string') return MENSAGENS_ERRO[e] || e;
  const detail = e?.response?.data?.detail ?? e?.detail ?? e;
  if (typeof detail === 'string') return MENSAGENS_ERRO[detail] || detail;
  // Erro de validação do FastAPI: detail é uma LISTA de itens {loc,msg,type}
  if (Array.isArray(detail)) {
    const msg = detail[0]?.msg;
    return typeof msg === 'string' ? msg : fallback;
  }
  const code = detail?.code;
  if (code && MENSAGENS_ERRO[code]) return MENSAGENS_ERRO[code];
  const out = detail?.message || (typeof code === 'string' ? code : null) || e?.message || fallback;
  // Garantia final: nunca devolve objeto (evita "[object Object]" no toast).
  return typeof out === 'string' ? out : fallback;
}
